import type { Task, WorkSession } from "../types/task";
import { topicService } from "./topicService";

const STORAGE_KEY = "timeTracker_tasks";

// On-brand color palette that complements gold (#fcd172) and teal (#25b2a0)
const BRAND_COLORS = [
  // Warm Golds and Ambers (complements primary gold)
  "hsl(45, 100%, 65%)", // Golden Amber
  "hsl(40, 100%, 60%)", // Rich Gold
  "hsl(50, 100%, 70%)", // Bright Gold
  "hsl(35, 100%, 55%)", // Deep Amber
  "hsl(55, 100%, 75%)", // Light Gold

  // Teals and Cyans (complements secondary teal)
  "hsl(180, 70%, 60%)", // Soft Teal
  "hsl(190, 80%, 65%)", // Ocean Teal
  "hsl(170, 75%, 55%)", // Deep Teal
  "hsl(200, 85%, 70%)", // Sky Teal
  "hsl(160, 65%, 50%)", // Forest Teal

  // Warm Corals and Peaches (warm accent)
  "hsl(15, 85%, 65%)", // Warm Coral
  "hsl(25, 90%, 70%)", // Peach
  "hsl(10, 80%, 60%)", // Deep Coral
  "hsl(20, 75%, 75%)", // Light Peach
  "hsl(5, 85%, 55%)", // Rich Coral

  // Muted Blues (cool accent)
  "hsl(210, 70%, 65%)", // Soft Blue
  "hsl(220, 75%, 60%)", // Muted Blue
  "hsl(200, 65%, 70%)", // Light Blue
  "hsl(230, 70%, 55%)", // Deep Blue
  "hsl(190, 60%, 75%)", // Pale Blue

  // Earthy Greens (natural accent)
  "hsl(120, 60%, 65%)", // Sage Green
  "hsl(130, 65%, 60%)", // Forest Green
  "hsl(110, 70%, 70%)", // Mint Green
  "hsl(140, 55%, 55%)", // Deep Green
  "hsl(100, 65%, 75%)", // Light Green

  // Warm Purples (sophisticated accent)
  "hsl(280, 60%, 65%)", // Soft Purple
  "hsl(270, 65%, 60%)", // Muted Purple
  "hsl(290, 70%, 70%)", // Light Purple
  "hsl(260, 55%, 55%)", // Deep Purple
  "hsl(300, 65%, 75%)", // Pale Purple
];

function getRandomColor(): string {
  // Randomly select from different color ranges for better variety
  const colorRanges = [
    // Warm Golds and Ambers (0-4)
    [0, 1, 2, 3, 4],
    // Teals and Cyans (5-9)
    [5, 6, 7, 8, 9],
    // Warm Corals and Peaches (10-14)
    [10, 11, 12, 13, 14],
    // Muted Blues (15-19)
    [15, 16, 17, 18, 19],
    // Earthy Greens (20-24)
    [20, 21, 22, 23, 24],
    // Warm Purples (25-29)
    [25, 26, 27, 28, 29],
  ];

  // Pick a random color range
  const randomRange =
    colorRanges[Math.floor(Math.random() * colorRanges.length)];
  // Pick a random color from that range
  const randomColorIndex =
    randomRange[Math.floor(Math.random() * randomRange.length)];

  const selectedColor = BRAND_COLORS[randomColorIndex];
  console.log(
    "Generated brand color:",
    selectedColor,
    "from index:",
    randomColorIndex
  );
  return selectedColor;
}

// Generate a deterministic base color for a project based on its ID
function getProjectBaseColor(projectId: string): string {
  // Simple hash function to convert project ID to a number
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    const char = projectId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use absolute value and modulo to select from BRAND_COLORS
  const colorIndex = Math.abs(hash) % BRAND_COLORS.length;
  return BRAND_COLORS[colorIndex];
}

// Generate a color variation for a task within a project
function getProjectColorVariation(baseColor: string, taskIndex: number): string {
  // Parse HSL color
  const hslMatch = baseColor.match(/hsl\(([^,]+),\s*([^,]+)%,\s*([^,]+)%\)/);
  if (!hslMatch) {
    // Fallback to base color if parsing fails
    return baseColor;
  }
  
  const h = parseFloat(hslMatch[1]);
  let s = parseFloat(hslMatch[2]);
  let l = parseFloat(hslMatch[3]);
  
  // Generate variation based on task index
  // Alternate between lighter and darker variations
  const variationIndex = taskIndex % 4;
  
  // Adjust lightness: ±10-15%
  const lightnessVariations = [0, 8, -8, 12];
  const lightnessAdjustment = lightnessVariations[variationIndex];
  l = Math.max(30, Math.min(80, l + lightnessAdjustment));
  
  // Adjust saturation: ±5-10%
  const saturationVariations = [0, 5, -5, -8];
  const saturationAdjustment = saturationVariations[variationIndex];
  s = Math.max(50, Math.min(100, s + saturationAdjustment));
  
  return `hsl(${h}, ${s}%, ${l}%)`;
}

// Migration function to update existing tasks with project-based colors
function migrateProjectColors(tasks: Task[]): Task[] {
  // Group tasks by projectId
  const projectGroups = new Map<string, Task[]>();
  const tasksWithoutProject: Task[] = [];
  
  tasks.forEach((task) => {
    if (task.projectId) {
      if (!projectGroups.has(task.projectId)) {
        projectGroups.set(task.projectId, []);
      }
      projectGroups.get(task.projectId)!.push(task);
    } else {
      tasksWithoutProject.push(task);
    }
  });
  
  // Migrate tasks with projects to use project-based colors
  const migratedTasks: Task[] = [];
  
  projectGroups.forEach((projectTasks, projectId) => {
    const baseColor = getProjectBaseColor(projectId);
    projectTasks.forEach((task, index) => {
      migratedTasks.push({
        ...task,
        color: getProjectColorVariation(baseColor, index),
      });
    });
  });
  
  // Keep tasks without projects as-is (they use random colors)
  migratedTasks.push(...tasksWithoutProject);
  
  return migratedTasks;
}

// Migration function to update existing tasks with new brand colors
function migrateTaskColors(tasks: Task[]): Task[] {
  return tasks.map((task) => {
    // Check if the task has an old color format (high saturation)
    const colorMatch = task.color.match(
      /hsl\(([^,]+),\s*([^,]+)%,\s*([^,]+)%\)/
    );
    if (colorMatch) {
      const saturation = parseFloat(colorMatch[2]);
      // If saturation is above 85%, it's likely an old vibrant color, so update it
      if (saturation > 85) {
        return {
          ...task,
          color: getRandomColor(),
        };
      }
    }
    return task;
  });
}

function calculateTotalDuration(sessions: WorkSession[]): number {
  return sessions.reduce((total, session) => {
    if (!session.endTime) return total;
    return (
      total +
      (new Date(session.endTime).getTime() -
        new Date(session.startTime).getTime())
    );
  }, 0);
}

// Helper function to check if a task is active (has session without endTime)
export function isTaskActive(task: Task): boolean {
  if (!task.sessions || task.sessions.length === 0) return false;
  const lastSession = task.sessions[task.sessions.length - 1];
  return !lastSession.endTime;
}

// Helper function to get active task from tasks array
export function getActiveTask(tasks: Task[]): Task | null {
  return tasks.find((task) => isTaskActive(task)) || null;
}

// Helper function to close open session in a task
export function closeOpenSession(task: Task, endTime?: string): Task {
  const now = endTime || new Date().toISOString();
  const updatedSessions = [...(task.sessions || [])];

  if (updatedSessions.length > 0) {
    const lastSession = updatedSessions[updatedSessions.length - 1];
    if (!lastSession.endTime) {
      updatedSessions[updatedSessions.length - 1] = {
        ...lastSession,
        endTime: now,
      };
    }
  }

  return {
    ...task,
    sessions: updatedSessions,
    duration: calculateTotalDuration(updatedSessions),
  };
}

// Validate and fix task state
export function validateAndFixTask(task: Task): Task {
  let fixedTask = { ...task };
  const sessions = [...(task.sessions || [])];

  // Fix: Completed tasks must have all sessions with endTime
  if (task.completed) {
    let hasOpenSession = false;
    fixedTask.sessions = sessions.map((session) => {
      if (!session.endTime) {
        hasOpenSession = true;
        return {
          ...session,
          endTime: new Date().toISOString(),
        };
      }
      return session;
    });

    if (hasOpenSession) {
      console.warn(
        `Task ${task.id} (${task.name}) was completed but had open session(s). Closed automatically.`
      );
    }
  }

  // Fix: Only last session can be open
  // Close all sessions except the last one if it's open
  if (sessions.length > 1) {
    const hasMultipleOpenSessions = sessions.some(
      (s, idx) => idx < sessions.length - 1 && !s.endTime
    );

    if (hasMultipleOpenSessions) {
      console.warn(
        `Task ${task.id} (${task.name}) had multiple open sessions. Closing all except the last one.`
      );
      fixedTask.sessions = sessions.map((session, idx) => {
        if (idx < sessions.length - 1 && !session.endTime) {
          return {
            ...session,
            endTime: new Date(session.startTime).toISOString(), // Use startTime as endTime (0 duration)
          };
        }
        return session;
      });
    }
  }

  // Recalculate duration
  fixedTask.duration = calculateTotalDuration(fixedTask.sessions);

  return fixedTask;
}

// Generate default data for development
function generateDefaultData(): Task[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Create tasks with realistic data for analytics testing
  const defaultTasks: Task[] = [
    {
      id: 1,
      name: "Design User Interface",
      duration: 2.5 * 60 * 60 * 1000, // 2.5 hours
      estimatedDuration: 2 * 60 * 60 * 1000, // 2 hours (overrun)
      completed: true,
      color: "hsl(180, 70%, 60%)", // Soft Teal
      topicId: "design",
      createdAt: new Date(
        today.getTime() - 2 * 24 * 60 * 60 * 1000
      ).toISOString(), // 2 days ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 2 * 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000
          ).toISOString(), // 2 days ago at 9 AM
          endTime: new Date(
            today.getTime() - 2 * 24 * 60 * 60 * 1000 + 11.5 * 60 * 60 * 1000
          ).toISOString(), // 2 days ago at 11:30 AM
        },
      ],
      overrunReasons: ["increased-scope", "more-complex"],
    },
    {
      id: 2,
      name: "Implement Authentication",
      duration: 3 * 60 * 60 * 1000, // 3 hours
      estimatedDuration: 3.5 * 60 * 60 * 1000, // 3.5 hours (under estimate)
      completed: true,
      color: "hsl(45, 100%, 65%)", // Golden Amber
      topicId: "coding",
      createdAt: new Date(
        today.getTime() - 1 * 24 * 60 * 60 * 1000
      ).toISOString(), // 1 day ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 1 * 24 * 60 * 60 * 1000 + 14 * 60 * 60 * 1000
          ).toISOString(), // 1 day ago at 2 PM
          endTime: new Date(
            today.getTime() - 1 * 24 * 60 * 60 * 1000 + 17 * 60 * 60 * 1000
          ).toISOString(), // 1 day ago at 5 PM
        },
      ],
    },
    {
      id: 3,
      name: "Write Documentation",
      duration: 1.5 * 60 * 60 * 1000, // 1.5 hours
      estimatedDuration: 1.5 * 60 * 60 * 1000, // 1.5 hours (perfect)
      completed: true,
      color: "hsl(280, 60%, 65%)", // Soft Purple
      topicId: "documentation",
      createdAt: new Date(
        today.getTime() - 3 * 24 * 60 * 60 * 1000
      ).toISOString(), // 3 days ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 3 * 24 * 60 * 60 * 1000 + 10 * 60 * 60 * 1000
          ).toISOString(), // 3 days ago at 10 AM
          endTime: new Date(
            today.getTime() - 3 * 24 * 60 * 60 * 1000 + 11.5 * 60 * 60 * 1000
          ).toISOString(), // 3 days ago at 11:30 AM
        },
      ],
    },
    {
      id: 4,
      name: "Code Review",
      duration: 45 * 60 * 1000, // 45 minutes
      estimatedDuration: 30 * 60 * 1000, // 30 minutes (overrun)
      completed: true,
      color: "hsl(15, 85%, 65%)", // Warm Coral
      topicId: "coding",
      createdAt: new Date(
        today.getTime() - 4 * 24 * 60 * 60 * 1000
      ).toISOString(), // 4 days ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 4 * 24 * 60 * 60 * 1000 + 16 * 60 * 60 * 1000
          ).toISOString(), // 4 days ago at 4 PM
          endTime: new Date(
            today.getTime() - 4 * 24 * 60 * 60 * 1000 + 16.75 * 60 * 60 * 1000
          ).toISOString(), // 4 days ago at 4:45 PM
        },
      ],
      overrunReasons: ["more-complex"],
    },
    {
      id: 5,
      name: "Team Meeting",
      duration: 60 * 60 * 1000, // 1 hour
      estimatedDuration: 60 * 60 * 1000, // 1 hour (perfect)
      completed: true,
      color: "hsl(190, 80%, 65%)", // Ocean Teal
      topicId: "meeting",
      createdAt: new Date(
        today.getTime() - 5 * 24 * 60 * 60 * 1000
      ).toISOString(), // 5 days ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 5 * 24 * 60 * 60 * 1000 + 13 * 60 * 60 * 1000
          ).toISOString(), // 5 days ago at 1 PM
          endTime: new Date(
            today.getTime() - 5 * 24 * 60 * 60 * 1000 + 14 * 60 * 60 * 1000
          ).toISOString(), // 5 days ago at 2 PM
        },
      ],
    },
    {
      id: 6,
      name: "Bug Fix",
      duration: 2 * 60 * 60 * 1000, // 2 hours
      estimatedDuration: 1 * 60 * 60 * 1000, // 1 hour (major overrun)
      completed: true,
      color: "hsl(10, 80%, 60%)", // Deep Coral
      topicId: "coding",
      createdAt: new Date(
        today.getTime() - 6 * 24 * 60 * 60 * 1000
      ).toISOString(), // 6 days ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 6 * 24 * 60 * 60 * 1000 + 15 * 60 * 60 * 1000
          ).toISOString(), // 6 days ago at 3 PM
          endTime: new Date(
            today.getTime() - 6 * 24 * 60 * 60 * 1000 + 17 * 60 * 60 * 1000
          ).toISOString(), // 6 days ago at 5 PM
        },
      ],
      overrunReasons: ["blocked", "more-complex"],
    },
    {
      id: 7,
      name: "Research New Technology",
      duration: 4 * 60 * 60 * 1000, // 4 hours
      estimatedDuration: 3 * 60 * 60 * 1000, // 3 hours (overrun)
      completed: true,
      color: "hsl(50, 100%, 70%)", // Bright Gold
      topicId: "research",
      createdAt: new Date(
        today.getTime() - 7 * 24 * 60 * 60 * 1000
      ).toISOString(), // 7 days ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 7 * 24 * 60 * 60 * 1000 + 8 * 60 * 60 * 1000
          ).toISOString(), // 7 days ago at 8 AM
          endTime: new Date(
            today.getTime() - 7 * 24 * 60 * 60 * 1000 + 12 * 60 * 60 * 1000
          ).toISOString(), // 7 days ago at 12 PM
        },
      ],
      overrunReasons: ["increased-scope"],
    },
    {
      id: 8,
      name: "Project Planning",
      duration: 1.5 * 60 * 60 * 1000, // 1.5 hours
      estimatedDuration: 2 * 60 * 60 * 1000, // 2 hours (under estimate)
      completed: true,
      color: "hsl(200, 85%, 70%)", // Sky Teal
      topicId: "planning",
      createdAt: new Date(
        today.getTime() - 8 * 24 * 60 * 60 * 1000
      ).toISOString(), // 8 days ago
      sessions: [
        {
          startTime: new Date(
            today.getTime() - 8 * 24 * 60 * 60 * 1000 + 9 * 60 * 60 * 1000
          ).toISOString(), // 8 days ago at 9 AM
          endTime: new Date(
            today.getTime() - 8 * 24 * 60 * 60 * 1000 + 10.5 * 60 * 60 * 1000
          ).toISOString(), // 8 days ago at 10:30 AM
        },
      ],
    },
    {
      id: 9,
      name: "Today's Task 1",
      duration: 1 * 60 * 60 * 1000, // 1 hour
      estimatedDuration: 1.5 * 60 * 60 * 1000, // 1.5 hours (under estimate)
      completed: false,
      color: "hsl(120, 70%, 60%)", // Green
      topicId: "coding",
      createdAt: new Date(today.getTime()).toISOString(), // Today
      sessions: [
        {
          startTime: new Date(
            today.getTime() + 10 * 60 * 60 * 1000
          ).toISOString(), // Today at 10 AM
          endTime: new Date(
            today.getTime() + 11 * 60 * 60 * 1000
          ).toISOString(), // Today at 11 AM
        },
      ],
    },
    {
      id: 10,
      name: "Today's Task 2",
      duration: 2 * 60 * 60 * 1000, // 2 hours
      estimatedDuration: 1.5 * 60 * 60 * 1000, // 1.5 hours (overrun)
      completed: true,
      color: "hsl(300, 70%, 60%)", // Purple
      topicId: "planning",
      createdAt: new Date(today.getTime()).toISOString(), // Today
      sessions: [
        {
          startTime: new Date(
            today.getTime() + 14 * 60 * 60 * 1000
          ).toISOString(), // Today at 2 PM
          endTime: new Date(
            today.getTime() + 16 * 60 * 60 * 1000
          ).toISOString(), // Today at 4 PM
        },
      ],
      overrunReasons: ["more-complex"],
    },
  ];

  return defaultTasks;
}

class TaskService {
  getTasks(): Task[] {
    try {
      const tasksJson = localStorage.getItem(STORAGE_KEY);
      if (!tasksJson) {
        // Check if we're running on localhost for development
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1" ||
          window.location.hostname.includes("localhost");

        if (isLocalhost) {
          // Generate and save default data for development
          const defaultTasks = generateDefaultData();
          this.saveTasks(defaultTasks);
          return defaultTasks;
        }

        // Return empty array if no tasks exist
        return [];
      }

      const tasks = JSON.parse(tasksJson);

      // Validate that tasks is an array
      if (!Array.isArray(tasks)) {
        console.error(
          "Invalid tasks data in localStorage, resetting to empty array"
        );
        localStorage.removeItem(STORAGE_KEY);
        return [];
      }

      // Migrate task colors to new vibrant palette
      let migratedTasks = migrateTaskColors(tasks);

      // Migration: Remap legacy 'idle' topicId to 'in-between'
      // Also handle legacy name-based topicId 'Idle' just in case
      const remappedTasks = migratedTasks.map((task) => {
        if (task.topicId === "idle" || task.topicId === "Idle") {
          return { ...task, topicId: "in-between" } as Task;
        }
        return task;
      });
      migratedTasks = remappedTasks;
      
      // Migrate tasks with projects to use project-based colors
      migratedTasks = migrateProjectColors(migratedTasks);

      // Handle migration of old tasks
      let migratedTasksList = migratedTasks.map((task: any) => {
        // Convert epoch timestamps to ISO strings if needed
        let migratedTask: any = {
          ...task,
          estimatedDuration: task.estimatedDuration || 0,
          sessions: task.sessions || [],
          topicId: task.topicId || "other",
          completed: task.completed || false,
          overrunReasons: task.overrunReasons || undefined,
        };

        // Migrate sessions if they use epoch timestamps
        if (migratedTask.sessions.length > 0) {
          migratedTask.sessions = migratedTask.sessions.map((session: any) => {
            // Check if startTime is a number (epoch timestamp)
            if (typeof session.startTime === "number") {
              return {
                startTime: new Date(session.startTime).toISOString(),
                endTime: session.endTime
                  ? new Date(session.endTime).toISOString()
                  : undefined,
              };
            }
            return session;
          });
        }

        // Migrate createdAt if it's a number
        if (typeof migratedTask.createdAt === "number") {
          migratedTask.createdAt = new Date(
            migratedTask.createdAt
          ).toISOString();
        } else if (!migratedTask.createdAt) {
          // Add createdAt if it doesn't exist
          migratedTask.createdAt = new Date().toISOString();
        }

        // Migration: Remove isActive, startTime, _manualDuration fields
        // Handle migration from isActive/startTime to session-based state
        const hadIsActive = "isActive" in task;
        const hadStartTime = "startTime" in task;
        const hadManualDuration = "_manualDuration" in task;

        if (hadIsActive || hadStartTime || hadManualDuration) {
          // Remove deprecated fields
          delete migratedTask.isActive;
          delete migratedTask.startTime;
          delete migratedTask._manualDuration;

          // Migration: If isActive was true but no open session exists, create one
          if (task.isActive && migratedTask.sessions.length > 0) {
            const lastSession =
              migratedTask.sessions[migratedTask.sessions.length - 1];
            if (lastSession.endTime) {
              // Task was marked active but last session was closed - create new session
              const startTime = task.startTime || new Date().toISOString();
              migratedTask.sessions.push({
                startTime:
                  typeof startTime === "number"
                    ? new Date(startTime).toISOString()
                    : startTime,
              });
            }
          }

          // Migration: If isActive was false but has open session, ensure it's closed
          if (!task.isActive && migratedTask.sessions.length > 0) {
            const lastSession =
              migratedTask.sessions[migratedTask.sessions.length - 1];
            if (!lastSession.endTime) {
              // Task was marked inactive but has open session - close it
              migratedTask.sessions[migratedTask.sessions.length - 1] = {
                ...lastSession,
                endTime: task.startTime
                  ? typeof task.startTime === "number"
                    ? new Date(task.startTime).toISOString()
                    : task.startTime
                  : new Date().toISOString(),
              };
            }
          }
        }

        // Migration: Completed tasks must have all sessions with endTime
        if (migratedTask.completed && migratedTask.sessions.length > 0) {
          migratedTask.sessions = migratedTask.sessions.map(
            (session: WorkSession) => {
              if (!session.endTime) {
                return {
                  ...session,
                  endTime: new Date().toISOString(),
                };
              }
              return session;
            }
          );
        }

        // Calculate duration from sessions
        migratedTask.duration = calculateTotalDuration(migratedTask.sessions);

        // Validate and fix task state
        migratedTask = validateAndFixTask(migratedTask as Task);

        // Only log in development
        if (import.meta.env.DEV) {
          console.log("Loaded task:", migratedTask);
        }

        return migratedTask as Task;
      });

      // Fix: Ensure only one task is active at a time
      const activeTasks = migratedTasksList.filter((task) =>
        isTaskActive(task)
      );
      if (activeTasks.length > 1) {
        console.warn(
          `Found ${activeTasks.length} active tasks. Closing all except the most recent one.`
        );
        // Keep the most recent active task (by last session startTime)
        const sortedActiveTasks = activeTasks.sort((a, b) => {
          const aLastSession = a.sessions[a.sessions.length - 1];
          const bLastSession = b.sessions[b.sessions.length - 1];
          return (
            new Date(bLastSession.startTime).getTime() -
            new Date(aLastSession.startTime).getTime()
          );
        });

        // Close all except the first one
        for (let i = 1; i < sortedActiveTasks.length; i++) {
          const taskToClose = sortedActiveTasks[i];
          const index = migratedTasksList.findIndex(
            (t) => t.id === taskToClose.id
          );
          if (index !== -1) {
            migratedTasksList[index] = closeOpenSession(taskToClose);
          }
        }
      }

      // Migration: Remove old taskQueue localStorage key if it exists
      if (localStorage.getItem("taskQueue")) {
        console.log("Removing old taskQueue localStorage key");
        localStorage.removeItem("taskQueue");
      }

      return migratedTasksList;
    } catch (error) {
      console.error("Error loading tasks from localStorage:", error);
      // Clear corrupted data and return empty array
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }
  }

  saveTasks(tasks: Task[]): void {
    // Only log in development
    if (import.meta.env.DEV) {
      console.log("Saving tasks:", tasks);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  }

  updateTask(updatedTask: Task): Task[] {
    const tasks = this.getTasks();
    const updatedTasks = tasks.map((task) => {
      if (task.id === updatedTask.id) {
        const result = { ...task, ...updatedTask, sessions: updatedTask.sessions || [] };
        
        // If projectId is explicitly undefined in updatedTask, remove it from result
        if ("projectId" in updatedTask && updatedTask.projectId === undefined) {
          const { projectId: _, ...taskWithoutProject } = result;
          // Use random color when project is removed
          return { ...taskWithoutProject, color: getRandomColor() } as Task;
        }
        
        // If projectId changed, recalculate color based on new project
        if ("projectId" in updatedTask && updatedTask.projectId !== task.projectId) {
          if (updatedTask.projectId) {
            // Get base color for the new project
            const baseColor = getProjectBaseColor(updatedTask.projectId);
            // Count existing tasks in this project (excluding current task) to determine variation index
            const projectTasks = tasks.filter(
              (t) => t.projectId === updatedTask.projectId && t.id !== task.id
            );
            const taskIndex = projectTasks.length;
            result.color = getProjectColorVariation(baseColor, taskIndex);
          } else {
            // Project removed, use random color
            result.color = getRandomColor();
          }
        }
        
        return result;
      }
      return task;
    });
    // Only log in development
    if (import.meta.env.DEV) {
      console.log("Updating task:", updatedTask);
      console.log("Updated tasks:", updatedTasks);
    }
    this.saveTasks(updatedTasks);
    return updatedTasks;
  }

  addTask(
    name: string,
    topicName: string = "Other",
    estimatedDuration: number = 0,
    projectId?: string
  ): Task[] {
    console.log("taskService.addTask called with:", {
      name,
      topicName,
      estimatedDuration,
      projectId,
    });

    const tasks = this.getTasks();
    console.log("Current tasks count:", tasks.length);

    const topic = topicService.getOrCreateTopic(topicName);
    console.log("Got or created topic:", topic);

    // Determine color based on whether task has a project
    let taskColor: string;
    if (projectId) {
      // Get base color for the project
      const baseColor = getProjectBaseColor(projectId);
      // Count existing tasks in this project to determine variation index
      const projectTasks = tasks.filter((t) => t.projectId === projectId);
      const taskIndex = projectTasks.length;
      taskColor = getProjectColorVariation(baseColor, taskIndex);
    } else {
      // Use random color for tasks without a project
      taskColor = getRandomColor();
    }

    const newTask: Task = {
      id: Math.max(0, ...tasks.map((t) => t.id)) + 1,
      name,
      duration: 0,
      estimatedDuration,
      completed: false,
      color: taskColor,
      sessions: [],
      topicId: topic.id,
      ...(projectId ? { projectId } : {}), // Only include projectId if it's truthy
      createdAt: new Date().toISOString(),
    };
    console.log("Created new task:", newTask);

    const updatedTasks = [...tasks, newTask];
    console.log("Saving updated tasks, count:", updatedTasks.length);
    this.saveTasks(updatedTasks);
    return updatedTasks;
  }

  deleteTask(taskId: number): Task[] {
    const tasks = this.getTasks();
    const updatedTasks = tasks.filter((task) => task.id !== taskId);
    this.saveTasks(updatedTasks);
    return updatedTasks;
  }

  resetAllTasks(): Task[] {
    // Clear all tasks by saving an empty array
    this.saveTasks([]);
    return [];
  }

  loadDefaultData(): Task[] {
    // Force load default data for development
    const defaultTasks = generateDefaultData();
    this.saveTasks(defaultTasks);
    console.log("Loaded default data:", defaultTasks);
    return defaultTasks;
  }
}

export const taskService = new TaskService();
